import type { ImportJobSummary } from '@api/lib/api-types';

export interface DoneCallbacks {
  summary: ImportJobSummary;
  onStartOver: () => void;
}

export function renderDoneScreen(host: HTMLElement, cb: DoneCallbacks): void {
  const s = cb.summary;
  const card = document.createElement('section');
  card.className = 'card';

  let title: string;
  let kind: 'success' | 'partial' | 'failed' | 'cancelled';
  switch (s.state) {
    case 'completed': title = 'Import complete'; kind = 'success'; break;
    case 'partial':   title = `Import finished with ${s.failedNotes} failure${s.failedNotes === 1 ? '' : 's'}`; kind = 'partial'; break;
    case 'failed':    title = 'Import failed'; kind = 'failed'; break;
    case 'cancelled': title = 'Import cancelled'; kind = 'cancelled'; break;
    default:          title = 'Done'; kind = 'success';
  }

  const h2 = document.createElement('h2');
  h2.textContent = title;

  const banner = document.createElement('div');
  banner.className = `banner ${kind}`;
  banner.setAttribute('role', kind === 'success' ? 'status' : 'alert');

  const summaryLine = document.createElement('p');
  summaryLine.textContent =
    `${s.importedNotes} of ${s.totalNotes} note${s.totalNotes === 1 ? '' : 's'} imported` +
    (s.failedNotes ? `, ${s.failedNotes} failed.` : '.');
  banner.append(summaryLine);

  card.append(h2, banner);

  if (kind === 'failed' && s.errorMessage) {
    const errP = document.createElement('p');
    errP.textContent = s.errorMessage;
    card.append(errP);
    const remediation = document.createElement('p');
    remediation.className = 'small muted';
    remediation.textContent = remediationFor(s.errorCode);
    card.append(remediation);
  } else if (kind === 'partial') {
    const note = document.createElement('p');
    note.className = 'small muted';
    note.textContent = `Failed notes were skipped — the rest were imported successfully. You can retry by starting a fresh import with a smaller .enex containing just the failed notes.`;
    card.append(note);
  } else if (kind === 'cancelled') {
    const note = document.createElement('p');
    note.className = 'small muted';
    note.textContent = `Notes imported before cancel will stay in OneNote.`;
    card.append(note);
  } else {
    const next = document.createElement('p');
    next.className = 'small muted';
    next.textContent = `Your file has been deleted from our servers.`;
    card.append(next);
  }

  const btnRow = document.createElement('div');
  btnRow.className = 'btn-row';
  const startOverBtn = document.createElement('button');
  startOverBtn.type = 'button';
  startOverBtn.className = 'btn btn-primary';
  startOverBtn.textContent = 'Import another file';
  startOverBtn.addEventListener('click', cb.onStartOver);
  btnRow.append(startOverBtn);
  card.append(btnRow);

  host.replaceChildren(card);
}

function remediationFor(code: string | null): string {
  switch (code) {
    case 'QUOTA_EXCEEDED':       return 'Free up space in OneNote, then start a new import.';
    case 'OAUTH_REFRESH_EXPIRED':return 'Sign in again and retry the import.';
    case 'PARSE_INVALID_ENEX':   return 'Re-export from Evernote (File → Export → ENEX) and try again.';
    case 'RATE_LIMIT_EXHAUSTED': return 'Microsoft is throttling. Wait a few minutes and start a new import.';
    case 'GRAPH_UNAVAILABLE':    return 'Microsoft is having an outage. Try again later.';
    default:                     return 'If this keeps happening, contact support with the error code above.';
  }
}
